import React, { useState, useEffect, useCallback } from 'react';
import Table1_2 from './Table1_2';
import {
  Users, Plus, Search, Filter, Edit, Trash2, Calendar, Mail, Building, User, ChevronLeft, ChevronRight, X, Clock
} from 'lucide-react';
import DateFormatter from './DateFormatter';
import { getAllEmployees, deleteEmployee, registerEmployee, updateEmployee } from '../services/HRstaff';
import { getAllProjects } from '../services/projectManagerAPIs';
import './CreateEmployee.css';
import { useNavigate } from 'react-router-dom';
import ShortViewProfile from './ShortViewProfile';
import ViewProfile from './ViewProfile';
import { useTheme } from '@mui/material';

const CreateEmployee = () => {
  const theme = useTheme();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterBy, setFilterBy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newImageUploaded, setNewImageUploaded] = useState(false);
  const [showAuditInfo, setShowAuditInfo] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const navigate = useNavigate();

  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '', role: '', departmentName: '',
    dateOfJoining: '', profileImage: null, position: '',
    dob: '', phone: '', office: '', gender: '', cin: '', diploma: '', cnss: '',
    projects: [],
  });

  const roles = ['HR', 'EMPLOYEE', 'MANAGER', 'DIRECTOR'];
  const position = ['Director', 'HR Manager', 'Project Manager', 'Customer Service', 'HR', 'Manager', 'Account Manager', 'Quality Reviewer', 'Bookkeeper', 'Planner', 'Personal Assistance', 'Software Engineer', 'Client Adviser', 'IT Professional'];
  const departments = ['IT', 'HR', 'Planning', 'Accounting', 'Customer Services', 'Billing'];
  const offices = ['Nador', 'Tangier', 'Netherlands'];
  const genders = ['MALE', 'FEMALE'];
  const diplomaOptions = ['High School', 'Technician', 'Bachelor', 'Engineer', 'Master', 'PhD', 'N/A'];

  const resetForm = useCallback(() => {
    setFormData({
      firstName: '', lastName: '', email: '', password: '', role: '', departmentName: '',
      dateOfJoining: '', profileImage: null, position: '', cnss: '',
      dob: '', phone: '', office: '', gender: '', cin: '', diploma: '',
      projects: [],
    });
    setFormErrors({});
    setEditingEmployee(null);
    setNewImageUploaded(false);
  }, []);

  const handleViewDetails = useCallback((id) => {
    navigate(`/view-profile/${id}`);
    setSelectedEmployeeId(null);
  }, [navigate]);

  // Format audit dates for display
  const formatAuditDate = (dateString) => {
    if (!dateString) return '20-08-2025';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const data = await getAllEmployees();
        const transformedData = data.map(employee => ({
          id: employee.employeeId,
          name: `${employee.firstName} ${employee.lastName}`,
          firstName: employee.firstName,
          lastName: employee.lastName,
          email: employee.user?.email || 'No email',
          role: employee.user?.role || 'No role',
          department: employee.department?.name || 'No department',
          joiningDate: employee.dateOfJoining,
          position: employee.position || '',
          dob: employee.dob || '',
          phone: employee.phone || '',
          office: employee.office || '',
          gender: employee.gender || '',
          cin: employee.cin || '',
          cnss: employee.cnss || '',
          diploma: employee.diploma || '',
          projects: employee.projects || [],
          profileImage: employee.image ? `/api/employees/${employee.employeeId}/image` : null,
          createdBy: employee.createdBy || 'SYSTEM',
          createdDate: employee.createdDate,
          lastModifiedBy: employee.lastModifiedBy || employee.createdBy || 'SYSTEM',
          lastModifiedDate: employee.lastModifiedDate || employee.createdDate,
        }));
        setEmployees(transformedData);
      } catch (err) {
        setError('Failed to load employees. Please try again later.');
        console.error('Fetch employees error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();

    const fetchProjects = async () => {
      try {
        const projects = await getAllProjects();
        setAvailableProjects(projects);
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    };
    fetchProjects();
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!editingEmployee) {
      if (!formData.email.trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
      if (!formData.password) errors.password = 'Password is required';
    }
    if (!formData.role) errors.role = 'Role is required';
    if (!formData.departmentName) errors.departmentName = 'Department is required';
    if (!formData.dateOfJoining) errors.dateOfJoining = 'Joining date is required';
    if (!formData.position) errors.position = 'Position is required';
    if (!formData.dob) errors.dob = 'Date of birth is required';
    if (!formData.phone) errors.phone = 'Phone number is required';
    if (!formData.office) errors.office = 'Office is required';
    if (!formData.gender) errors.gender = 'Gender is required';
    if (!formData.cin) errors.cin = 'CIN is required';
    if (!formData.diploma) errors.diploma = 'Diploma is required';
    if (formData.profileImage && newImageUploaded && formData.profileImage.size > 3 * 1024 * 1024) {
      errors.profileImage = 'Image size must be less than 3MB';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, editingEmployee, newImageUploaded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingEmployee) {
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          position: formData.position,
          dob: formData.dob,
          dateOfJoining: formData.dateOfJoining,
          phone: formData.phone,
          office: formData.office,
          gender: formData.gender,
          cin: formData.cin,
          diploma: formData.diploma,
          cnss: formData.cnss,
          departmentName: formData.departmentName,
          role: formData.role,
          contractType: "PERMANENT",
          contractStartDate: formData.dateOfJoining,
          contractStatus: "ACTIVE",
          projects: formData.projects || [],
        };
        if (newImageUploaded && formData.profileImage) {
          updateData.image = formData.profileImage;
        }
        await updateEmployee(editingEmployee.id, updateData);
      } else {
        const employeeData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          position: formData.position,
          dob: formData.dob,
          dateOfJoining: formData.dateOfJoining,
          phone: formData.phone,
          office: formData.office,
          gender: formData.gender,
          cin: formData.cin,
          diploma: formData.diploma,
          departmentName: formData.departmentName,
          role: formData.role,
          contractType: "PERMANENT",
          contractStartDate: formData.dateOfJoining,
          contractStatus: "ACTIVE",
          contractFile: "default_contract.pdf",
          cnss: formData.cnss || "N/A",
          projects: formData.projects || [],
        };
        if (formData.profileImage) {
          employeeData.image = formData.profileImage;
        }
        await registerEmployee(employeeData);
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      const data = await getAllEmployees();
      const transformedData = data.map(employee => ({
        id: employee.employeeId,
        name: `${employee.firstName} ${employee.lastName}`,
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.user?.email || 'No email',
        role: employee.user?.role || 'No role',
        department: employee.department?.name || 'No department',
        joiningDate: employee.dateOfJoining,
        position: employee.position || '',
        dob: employee.dob || '',
        phone: employee.phone || '',
        office: employee.office || '',
        gender: employee.gender || '',
        cin: employee.cin || '',
        cnss: employee.cnss || '',
        diploma: employee.diploma || '',
        projects: employee.projects || [],
        profileImage: employee.image ? `/api/employees/${employee.employeeId}/image` : null,
        createdBy: employee.createdBy || 'SYSTEM',
        createdDate: employee.createdDate,
        lastModifiedBy: employee.lastModifiedBy || employee.createdBy || 'SYSTEM',
        lastModifiedDate: employee.lastModifiedDate || employee.createdDate,
      }));
      setEmployees(transformedData);

      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Submission error:', error);
      setError(`Failed to ${editingEmployee ? 'update' : 'save'} employee: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 3 * 1024 * 1024) {
        setFormErrors(prev => ({ ...prev, profileImage: 'Image size must be less than 3MB' }));
        return;
      }
      setLoading(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.replace(/^data:image\/[a-z]+;base64,/, '');
        setFormData(prev => ({ ...prev, profileImage: base64String }));
        setNewImageUploaded(true);
        setFormErrors(prev => {
          const { profileImage, ...rest } = prev;
          return rest;
        });
        setLoading(false);
        validateForm();
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, profileImage: editingEmployee ? editingEmployee.profileImage : null }));
      setNewImageUploaded(false);
      setFormErrors(prev => {
        const { profileImage, ...rest } = prev;
        return rest;
      });
      validateForm();
    }
  };

  const handleEdit = (employee) => {
    setFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      password: '',
      role: employee.role || '',
      departmentName: employee.department || '',
      dateOfJoining: employee.joiningDate || '',
      profileImage: employee.profileImage || null,
      position: employee.position || '',
      dob: employee.dob || '',
      phone: employee.phone || '',
      office: employee.office || '',
      gender: employee.gender || '',
      cin: employee.cin || '',
      cnss: employee.cnss || '',
      diploma: employee.diploma || '',
      projects: employee.projects ? (Array.isArray(employee.projects) ? employee.projects.map(p => typeof p === 'string' ? p : p.projectName) : []) : []
    });
    setEditingEmployee(employee);
    setNewImageUploaded(false);
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await deleteEmployee(id);
        setEmployees(prev => prev.filter(emp => emp.id !== id));
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        setError('Failed to delete employee. Please try again.');
      }
    }
  };

  const handleViewClick = useCallback((employee) => {

    navigate(`/view-profile/${employee.id}`);
  }, [navigate]);


  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleCloseView = useCallback(() => {
    setSelectedEmployeeId(null);
  }, []);

  const filteredAndSortedEmployees = employees
    .filter(emp => {
      const matchesSearch =
        emp.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.lastModifiedBy?.toLowerCase().includes(searchTerm.toLowerCase());

      if (filterBy === 'all') return matchesSearch;
      return matchesSearch && (emp.department === filterBy || emp.role === filterBy);
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'joiningDate' || sortBy === 'dob' || sortBy === 'createdDate' || sortBy === 'lastModifiedDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      return sortOrder === 'asc' ? (aVal < bVal ? -1 : 1) : (aVal > bVal ? -1 : 1);
    });

  const totalPages = Math.ceil(filteredAndSortedEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredAndSortedEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Define table columns including audit information
  const getTableColumns = () => {
    const baseColumns = [
      {
        header: 'Name',
        key: 'name',
        onClick: () => handleSort('name'),
        sortIcon: sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓'),
        render: (item) => <><User size={16} /> {item.name}</>
      },
      {
        header: 'Email',
        key: 'email',
        onClick: () => handleSort('email'),
        sortIcon: sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓'),
        render: (item) => <><Mail size={16} /> {item.email}</>
      },
      {
        header: 'Role',
        key: 'role',
        onClick: () => handleSort('role'),
        sortIcon: sortBy === 'role' && (sortOrder === 'asc' ? '↑' : '↓')
      },
      {
        header: 'Department',
        key: 'department',
        onClick: () => handleSort('department'),
        sortIcon: sortBy === 'department' && (sortOrder === 'asc' ? '↑' : '↓'),
        render: (item) => <span className={`badge badge-${item.department.toLowerCase()}`}>{item.department}</span>
      },
      {
        header: 'Joining Date',
        key: 'joiningDate',
        onClick: () => handleSort('joiningDate'),
        sortIcon: sortBy === 'joiningDate' && (sortOrder === 'asc' ? '↑' : '↓'),
        render: (item) => <><Calendar size={16} /> <DateFormatter date={item.joiningDate} /></>
      },
    ];

    if (showAuditInfo) {
      baseColumns.push(
        {
          header: 'Created By',
          key: 'createdBy',
          onClick: () => handleSort('createdBy'),
          sortIcon: sortBy === 'createdBy' && (sortOrder === 'asc' ? '↑' : '↓'),
          render: (item) => <><User size={16} /> {item.createdBy}</>
        },
        {
          header: 'Created Date',
          key: 'createdDate',
          onClick: () => handleSort('createdDate'),
          sortIcon: sortBy === 'createdDate' && (sortOrder === 'asc' ? '↑' : '↓'),
          render: (item) => <><Clock size={16} /> {formatAuditDate(item.createdDate)}</>
        },
        {
          header: 'Last Modified By',
          key: 'lastModifiedBy',
          onClick: () => handleSort('lastModifiedBy'),
          sortIcon: sortBy === 'lastModifiedBy' && (sortOrder === 'asc' ? '↑' : '↓'),
          render: (item) => <><User size={16} /> {item.lastModifiedBy}</>
        },
        {
          header: 'Last Modified Date',
          key: 'lastModifiedDate',
          onClick: () => handleSort('lastModifiedDate'),
          sortIcon: sortBy === 'lastModifiedDate' && (sortOrder === 'asc' ? '↑' : '↓'),
          render: (item) => <><Clock size={16} /> {formatAuditDate(item.lastModifiedDate)}</>
        }
      );
    }

    return baseColumns;
  };

  if (loading) return <div className="loading">Loading employees...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className={`ce-app ${theme?.palette?.mode === 'dark' ? 'dark' : ''}`}>
      <div className="ce-container">
        <div className="ce-header">
          <div className="ce-search-box">
            <Search className="ce-search-icon" size={20} />
            <input
              type="text"
              className="ce-search-input"
              placeholder="Search employees, creators, modifiers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
          >
            <option value="all">All Departments</option>
            {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
          </select>

          <button
            className={`ce-btn ${showAuditInfo ? 'btn-secondary' : 'btn-outline'}`}
            onClick={() => setShowAuditInfo(!showAuditInfo)}
            style={{ marginLeft: 10 }}
          >
            <Clock size={18} /> {showAuditInfo ? 'Hide' : 'Show'} Audit Info
          </button>

          <button className="ce-btn btn-primary" onClick={() => setShowForm(true)} style={{ marginLeft: 10 }}>
            <Plus size={18} /> Add Employee
          </button>
        </div>

        <Table1_2
          data={paginatedEmployees}
          columns={getTableColumns()}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleViewClick}
        />

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}

        {showSuccess && (
          <div className="success-message">
            Employee {editingEmployee ? 'updated' : 'created'} successfully!
          </div>
        )}

        {showForm && (
          <div className="form-overlay" onClick={handleCloseForm}>
            <div className="form-modal" onClick={e => e.stopPropagation()}>
              <button className="close-btn" onClick={handleCloseForm}><X size={20} /></button>
              <h2>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-section">
                  <h3>👤 Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        id="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="First name"
                      />
                      {formErrors.firstName && <div className="form-error">{formErrors.firstName}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        id="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Last name"
                      />
                      {formErrors.lastName && <div className="form-error">{formErrors.lastName}</div>}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="dob">Date of Birth *</label>
                      <input
                        id="dob"
                        type="date"
                        value={formData.dob}
                        onChange={(e) => handleInputChange('dob', e.target.value)}
                      />
                      {formErrors.dob && <div className="form-error">{formErrors.dob}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="gender">Gender *</label>
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                      >
                        <option value="">Select Gender</option>
                        {genders.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                      {formErrors.gender && <div className="form-error">{formErrors.gender}</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cin">CIN *</label>
                    <input
                      id="cin"
                      type="text"
                      value={formData.cin}
                      onChange={(e) => handleInputChange('cin', e.target.value)}
                      placeholder="Enter CIN"
                    />
                    {formErrors.cin && <div className="form-error">{formErrors.cin}</div>}
                  </div>
                </div>

                <div className="form-section">
                  <h3>🔐 Account Information</h3>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email"
                      disabled={!!editingEmployee}
                    />
                    {formErrors.email && <div className="form-error">{formErrors.email}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">{editingEmployee ? 'Password' : 'Password *'}</label>
                    <input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder={editingEmployee ? "Password cannot be changed" : "Enter password"}
                      disabled={!!editingEmployee}
                    />
                    {formErrors.password && <div className="form-error">{formErrors.password}</div>}
                  </div>
                </div>

                <div className="form-section">
                  <h3>💼 Professional Information</h3>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="position">Position *</label>
                      <select
                        id="position"
                        value={formData.position}
                        onChange={(e) => handleInputChange('position', e.target.value)}
                      >
                        <option value="">Select Position</option>
                        {position.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                      </select>
                      {formErrors.position && <div className="form-error">{formErrors.position}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="departmentName">Department *</label>
                      <select
                        id="departmentName"
                        value={formData.departmentName}
                        onChange={(e) => handleInputChange('departmentName', e.target.value)}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                      </select>
                      {formErrors.departmentName && <div className="form-error">{formErrors.departmentName}</div>}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="dateOfJoining">Joining Date *</label>
                      <input
                        id="dateOfJoining"
                        type="date"
                        value={formData.dateOfJoining}
                        onChange={(e) => handleInputChange('dateOfJoining', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                      />
                      {formErrors.dateOfJoining && <div className="form-error">{formErrors.dateOfJoining}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="office">Office *</label>
                      <select
                        id="office"
                        value={formData.office}
                        onChange={(e) => handleInputChange('office', e.target.value)}
                      >
                        <option value="">Select Office</option>
                        {offices.map(office => <option key={office} value={office}>{office}</option>)}
                      </select>
                      {formErrors.office && <div className="form-error">{formErrors.office}</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      id="phone"
                      type="text"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter phone number"
                    />
                    {formErrors.phone && <div className="form-error">{formErrors.phone}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="cnss">CNSS</label>
                    <input
                      id="cnss"
                      type="text"
                      value={formData.cnss}
                      onChange={(e) => handleInputChange('cnss', e.target.value)}
                      placeholder="Enter CNSS number"
                    />
                    {formErrors.cnss && <div className="form-error">{formErrors.cnss}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="role">Role *</label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                    >
                      <option value="">Select Role</option>
                      {roles.map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                    {formErrors.role && <div className="form-error">{formErrors.role}</div>}
                  </div>
                </div>

                <div className="form-section">
                  <h3>🎓 Diploma</h3>
                  <div className="form-group">
                    <label htmlFor="diploma">Education Level *</label>
                    <select
                      id="diploma"
                      value={formData.diploma}
                      onChange={(e) => handleInputChange('diploma', e.target.value)}
                    >
                      <option value="">Select Education Level</option>
                      {diplomaOptions.map(diploma => <option key={diploma} value={diploma}>{diploma}</option>)}
                    </select>
                    {formErrors.diploma && <div className="form-error">{formErrors.diploma}</div>}
                  </div>
                </div>

                <div className="form-section">
                  <h3>📁 Projects</h3>
                  <div className="form-group">
                    <label>Assigned Projects</label>
                    <div className="projects-selector-container" style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      marginTop: '8px'
                    }}>
                      <div className="search-box" style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                        <input
                          type="text"
                          placeholder="Search projects..."
                          value={projectSearchTerm}
                          onChange={(e) => setProjectSearchTerm(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px 10px 36px',
                            borderRadius: '10px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.9rem',
                            outline: 'none',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>
                      
                      {formData.projects?.length > 0 && (
                        <div className="selected-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '6px', borderBottom: '1px solid #f1f5f9', marginBottom: '4px' }}>
                          {formData.projects.map(name => (
                            <div key={name} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '5px 12px',
                              backgroundColor: '#0ea5e9',
                              color: 'white',
                              borderRadius: '25px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              {name}
                              <X 
                                size={14} 
                                style={{ cursor: 'pointer' }} 
                                onClick={() => {
                                  const nextProjects = (formData.projects || []).filter(pName => pName !== name);
                                  handleInputChange('projects', nextProjects);
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="projects-list" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                        gap: '10px',
                        maxHeight: '180px',
                        overflowY: 'auto',
                        padding: '4px'
                      }}>
                        {availableProjects
                          .filter(p => !projectSearchTerm || p.projectName.toLowerCase().includes(projectSearchTerm.toLowerCase()))
                          .map(project => {
                            const isSelected = formData.projects?.includes(project.projectName);
                            return (
                              <div 
                                key={project.projectId}
                                onClick={() => {
                                  const currentProjects = formData.projects || [];
                                  const nextProjects = isSelected
                                    ? currentProjects.filter(p => p !== project.projectName)
                                    : [...currentProjects, project.projectName];
                                  handleInputChange('projects', nextProjects);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '10px 15px',
                                  borderRadius: '12px',
                                  border: '1px solid',
                                  borderColor: isSelected ? '#0ea5e9' : '#e2e8f0',
                                  backgroundColor: isSelected ? '#f0f9ff' : '#ffffff',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  gap: '10px',
                                  boxShadow: isSelected ? '0 2px 4px rgba(14, 165, 233, 0.1)' : 'none'
                                }}
                              >
                                <div style={{
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '5px',
                                  border: '2px solid',
                                  borderColor: isSelected ? '#0ea5e9' : '#cbd5e1',
                                  backgroundColor: isSelected ? '#0ea5e9' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white'
                                }}>
                                  {isSelected && <Plus size={14} style={{ transform: 'rotate(45deg)' }} />}
                                </div>
                                <span style={{ 
                                  fontSize: '0.85rem', 
                                  fontWeight: isSelected ? '600' : '500',
                                  color: isSelected ? '#0369a1' : '#475569',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {project.projectName}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>📷 Profile Image</h3>
                  <div className="image-upload-container">
                    <div className="image-preview">
                      {formData.profileImage ? (
                        <img
                          src={newImageUploaded ? `data:image/jpeg;base64,${formData.profileImage}` : formData.profileImage}
                          alt="Preview"
                          className="image-preview-img"
                        />
                      ) : (
                        <div className="image-placeholder"><User size={40} /><span>No image selected</span></div>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageUpload} id="image-upload" className="image-input" />
                    <label htmlFor="image-upload" className="image-label">
                      {formData.profileImage ? 'Change Image' : 'Upload Image'}
                    </label>
                  </div>
                  {formErrors.profileImage && <div className="form-error">{formErrors.profileImage}</div>}
                </div>

                {editingEmployee && (
                  <div className="form-section">
                    <h3>📋 Audit Information</h3>
                    <div className="audit-info">
                      <div className="audit-item">
                        <strong>Created By:</strong> {editingEmployee.createdBy}
                      </div>
                      <div className="audit-item">
                        <strong>Created Date:</strong> {formatAuditDate(editingEmployee.createdDate)}
                      </div>
                      <div className="audit-item">
                        <strong>Last Modified By:</strong> {editingEmployee.lastModifiedBy}
                      </div>
                      <div className="audit-item">
                        <strong>Last Modified Date:</strong> {formatAuditDate(editingEmployee.lastModifiedDate)}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={Object.values(formErrors).some(error => error)}>
                    {editingEmployee ? 'Update Employee' : 'Add Employee'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* {selectedEmployeeId && (
          <ShortViewProfile
            employeeId={selectedEmployeeId}
            onViewDetails={handleViewDetails}
            onClose={handleCloseView}
          />
        )} */}
      </div>
    </div>
  );
};

export default CreateEmployee;